import { useDropdown } from '../../../../widgets/hooks/useDropdown';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, Suspense, useCallback, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { spin } from '../../styles/animations';
import { GenerationResult, ToolType, VideoResult } from '../../types';
import { downloadFile } from '../../utils/downloadHelpers';
import { ImageLightbox } from '../layout/imageLightbox';
import { ActionButton, DropdownDivider, DropdownMenuItem, ExpandButton } from '../shared/sharedStyles';

const GlbViewer = React.lazy(() => import('../shared/glbViewer'));

const MODEL_3D_TOOLS: ToolType[] = ['text-to-3d', 'image-to-3d'];

// Icons
import ArrowRightIcon from 'src/shared/icons/arrow-right-solid.svg';
import DownloadIcon from 'src/shared/icons/download-solid.svg';
import EllipsisVIcon from 'src/shared/icons/ellipsis-v-solid.svg';
import ExclamationIcon from 'src/shared/icons/exclamation-triangle-solid.svg';
import ExpandIcon from 'src/shared/icons/expand-solid.svg';
import PlayIcon from 'src/shared/icons/play-solid.svg';
import TrashIcon from 'src/shared/icons/trash-alt-solid.svg';

export const ResultCard = styled.div<{ $status: string }>`
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColorBase};
    aspect-ratio: 1;

    ${(props) =>
        props.$status === 'failed' &&
        `
		border-color: #ef4444;
		background: #fef2f2;
	`}
`;

const ResultImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ResultVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

export const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    gap: 12px;
`;

export const Spinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid ${(props) => props.theme.app.borderColor};
    border-top-color: ${(props) => props.theme.app.primary};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

export const LoadingText = styled.div`
    font-size: 12px;
    color: #78909c;
`;

const ErrorOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px;
    text-align: center;
    gap: 8px;
`;

const ErrorIconContainer = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fee2e2;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 20px;
        height: 20px;
        fill: #ef4444;
    }
`;

const ErrorText = styled.div`
    font-size: 12px;
    color: #991b1b;
`;

const MenuButtonWrapper = styled.div`
    position: absolute;
    bottom: 8px;
    left: 8px;
    z-index: 2;
`;

const Timestamp = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 10px;
    color: white;
    background: rgba(0, 0, 0, 0.5);
    padding: 2px 6px;
    border-radius: 4px;
`;

export interface ResultItemProps {
    result: GenerationResult | VideoResult;
    onDelete?: (resultId: string) => void;
    onUseAsInput?: (imageUrl: string) => void;
    showTimestamp?: boolean;
}

/** Individual result item component with consistent styling */
export const ResultItem: FC<ResultItemProps> = ({ result, onDelete, onUseAsInput, showTimestamp = true }) => {
    const [showLightbox, setShowLightbox] = useState(false);
    const [setOpen, setClose, isOpen, Dropdown] = useDropdown();

    const isModel = MODEL_3D_TOOLS.includes(result.tool);

    const handleDownload = useCallback(async () => {
        const isVideoResult = 'videoUrl' in result && result.videoUrl;
        const url = isVideoResult ? result.videoUrl! : result.url;
        const extension = isVideoResult ? 'mp4' : isModel ? 'glb' : 'png';
        try {
            await downloadFile(url, `ai-result-${result.id}.${extension}`);
        } catch (error) {
            console.error('Failed to download file:', error);
        }
    }, [result, isModel]);

    const handleUseAsInput = useCallback(() => {
        if (onUseAsInput) {
            onUseAsInput(result.url);
        }
    }, [result.url, onUseAsInput]);

    const formatTime = (date: Date) => {
        return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleMenuToggle = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isOpen) {
                setClose();
            } else {
                setOpen(e.currentTarget as HTMLElement, 'top', 'left');
            }
        },
        [isOpen, setOpen, setClose]
    );

    const isVideo = 'videoUrl' in result && !!result.videoUrl;
    const mediaUrl = isVideo ? result.videoUrl! : result.url;

    const renderMedia = () => {
        if (isVideo) return <ResultVideo src={result.videoUrl} muted loop />;
        if (isModel)
            return (
                <Suspense fallback={null}>
                    <GlbViewer url={result.url} />
                </Suspense>
            );
        return <ResultImage src={result.url} alt='Generated result' />;
    };

    return (
        <ResultCard $status={result.status}>
            {result.status === 'completed' && (
                <>
                    {renderMedia()}
                    {showTimestamp && <Timestamp>{formatTime(result.timestamp)}</Timestamp>}

                    <MenuButtonWrapper>
                        <ActionButton onClick={handleMenuToggle}>
                            <EllipsisVIcon />
                        </ActionButton>
                    </MenuButtonWrapper>

                    {isOpen && (
                        <Dropdown>
                            {onUseAsInput && !isVideo && !isModel && (
                                <>
                                    <DropdownMenuItem onClick={handleUseAsInput}>
                                        <ArrowRightIcon />
                                        {T._('Use as Input', 'Admin')}
                                    </DropdownMenuItem>
                                    <DropdownDivider />
                                </>
                            )}
                            <DropdownMenuItem onClick={handleDownload}>
                                <DownloadIcon />
                                {T._('Download', 'Admin')}
                            </DropdownMenuItem>
                            {onDelete && (
                                <>
                                    <DropdownDivider />
                                    <DropdownMenuItem $danger onClick={() => onDelete(result.id)}>
                                        <TrashIcon />
                                        {T._('Delete', 'Admin')}
                                    </DropdownMenuItem>
                                </>
                            )}
                        </Dropdown>
                    )}

                    <ZTooltip
                        position='bottom'
                        title={isVideo ? T._('Play Video', 'Admin') : T._('View Full Size', 'Admin')}
                        enterDelay={0}
                        exitDelay={0}
                        interactive={false}
                    >
                        <ExpandButton onClick={() => setShowLightbox(true)}>{isVideo ? <PlayIcon /> : <ExpandIcon />}</ExpandButton>
                    </ZTooltip>

                    {showLightbox && (
                        <ImageLightbox imageUrl={mediaUrl} alt='Generated result' isVideo={isVideo} isModel={isModel} onClose={() => setShowLightbox(false)} />
                    )}
                </>
            )}
            {result.status === 'processing' && (
                <LoadingOverlay>
                    <Spinner />
                    <LoadingText>{T._('Processing...', 'Admin')}</LoadingText>
                </LoadingOverlay>
            )}
            {result.status === 'failed' && (
                <ErrorOverlay>
                    <ErrorIconContainer>
                        <ExclamationIcon />
                    </ErrorIconContainer>
                    <ErrorText>{result.error || T._('Generation failed', 'Admin')}</ErrorText>
                </ErrorOverlay>
            )}
        </ResultCard>
    );
};

export default ResultItem;
